import React, { useState } from 'react';
import './style.scoped.scss';
import RegCityLogo from '../../components/Account/RegCityLogo';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../assets/constants/strings';
import { FaArrowLeft, FaCheck, FaRocket, FaRegClock, FaArrowRight } from 'react-icons/fa';
import { connect } from 'react-redux';
import ContactUsModal from './ContactUsModal';
import {
  isReaderEnterprise,
  isReaderTrial,
  readerTrialDaysLeft,
  readerAccessLapsed,
} from '../../services/entitlements';

// RegulationCity reader plans — no self-serve pricing. Two states, mirroring the
// developer API: a time-boxed Trial (self-serve) and a sales-provisioned
// Enterprise tier (Contact Us → enquiry → we provision). This replaces the old
// Paddle Free/Pro/Business price ladder.
const TRIAL_FEATURES = [
  'Full access while you evaluate',
  'The complete regulatory library',
  'Search, bookmarks & summaries',
  'No card required',
];

const ENTERPRISE_FEATURES = [
  'Everything in the Trial, unlimited',
  'Seats & access tailored to your team',
  'Priority support',
  'Pricing scoped to your needs',
];

const Plans = ({ user, isUpdate }) => {
  const [showContact, setShowContact] = useState(false);

  const enterprise = isReaderEnterprise(user);
  const trial = isReaderTrial(user);
  const daysLeft = readerTrialDaysLeft(user);
  // Trial (or other reader access) has lapsed — no current access, steer them to Enterprise.
  const lapsed = readerAccessLapsed(user);

  return (
    <div className='rpl'>
      <div className='rpl__logo'>
        {isUpdate && (
          <Link to={ROUTES.MEMBERSHIP_INFO} className='back-button backButton'>
            <FaArrowLeft />
            <p>Back</p>
          </Link>
        )}
        <RegCityLogo isPlans />
      </div>

      {/* ── Current-plan banner ── */}
      <header className='rpl__hero'>
        <span className='rpl__pill'>
          <span className='rpl__dot' />
          {enterprise
            ? 'You’re on Enterprise'
            : lapsed
            ? 'Your access has ended'
            : 'You’re on the Trial'}
        </span>
        <h1 className='rpl__title'>
          {enterprise ? (
            <>Your team is set up with <em>RegulationCity</em></>
          ) : lapsed ? (
            <>Keep your access to <em>RegulationCity</em></>
          ) : (
            <>Explore all of <em>RegulationCity</em></>
          )}
        </h1>
        <p className='rpl__sub'>
          {enterprise
            ? 'You’re on an Enterprise plan — full access with seats, support and terms tailored to your team.'
            : lapsed
            ? 'Your access has ended, so it’s paused. Enterprise gives your team ongoing access with tailored seats, support and pricing — contact us to get set up.'
            : trial
            ? 'You have full access while you evaluate. When you’re ready to roll it out to your team, Enterprise scales with tailored access, support and pricing.'
            : 'When you’re ready to roll RegulationCity out to your team, Enterprise scales with tailored access, support and pricing.'}
        </p>
        {(trial || lapsed) && (
          <div className={`rpl__trialtag${daysLeft <= 3 || lapsed ? ' is-soon' : ''}`}>
            <FaRegClock />
            {lapsed
              ? 'Your access has ended'
              : daysLeft > 0
              ? `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left on your trial`
              : 'Your trial has ended'}
          </div>
        )}
      </header>

      {/* ── Two cards ── */}
      <section className='rpl__cards'>
        {/* TRIAL */}
        <div
          className={`rpl__card rpl__card--trial${
            !enterprise && !lapsed ? ' is-current' : ''
          }`}
        >
          {!enterprise && !lapsed && (
            <span className='rpl__flag'>
              <FaCheck /> Your plan
            </span>
          )}
          <div className='rpl__card-icon is-trial'>
            <FaRegClock />
          </div>
          <h3 className='rpl__card-name'>Trial</h3>
          <p className='rpl__card-tag'>Evaluate RegulationCity</p>
          <p className='rpl__card-price'>Free</p>
          <div className='rpl__card-actions'>
            {lapsed ? (
              <span
                className='rpl__btn rpl__btn--teal rpl__btn--block'
                aria-disabled='true'
                style={{ opacity: 0.5, pointerEvents: 'none' }}
              >
                Trial ended
              </span>
            ) : (
              <Link to={ROUTES.HOME} className='rpl__btn rpl__btn--teal rpl__btn--block'>
                Go to the app
              </Link>
            )}
          </div>
          <hr />
          <ul className='rpl__feat'>
            {TRIAL_FEATURES.map((f, i) => (
              <li key={i}>
                <span className='rpl__tick'><FaCheck /></span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* ENTERPRISE */}
        <div className={`rpl__card rpl__card--ent${enterprise ? ' is-current' : ''}`}>
          {enterprise ? (
            <span className='rpl__flag'>
              <FaCheck /> Your plan
            </span>
          ) : (
            <span className='rpl__ribbon'>For teams</span>
          )}
          <div className='rpl__card-icon is-ent'>
            <FaRocket />
          </div>
          <h3 className='rpl__card-name'>Enterprise</h3>
          <p className='rpl__card-tag'>Roll it out to your team</p>
          <p className='rpl__card-price'>
            Contact us <small>· tailored</small>
          </p>
          <div className='rpl__card-actions'>
            {enterprise ? (
              <Link to={ROUTES.HOME} className='rpl__btn rpl__btn--navy rpl__btn--block'>
                Go to the app
              </Link>
            ) : (
              <button
                type='button'
                className='rpl__btn rpl__btn--navy rpl__btn--block'
                onClick={() => setShowContact(true)}
              >
                Contact Us
              </button>
            )}
          </div>
          <hr />
          <ul className='rpl__feat'>
            {ENTERPRISE_FEATURES.map((f, i) => (
              <li key={i}>
                <span className='rpl__tick'><FaCheck /></span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <p className='rpl__footnote'>
        Questions about an Enterprise plan?{' '}
        <button type='button' className='rpl__link' onClick={() => setShowContact(true)}>
          Contact us <FaArrowRight />
        </button>{' '}
        and we’ll help you scope the right setup.
      </p>

      <ContactUsModal show={showContact} handleClose={() => setShowContact(false)} />
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user.user,
});

export default connect(mapStateToProps)(Plans);
